import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  Event,
  RouterOutlet,
} from '@angular/router';
import { LoadingService } from '@services/loading.service';
import { setBrandingColor } from '@utils/branding';
import { Branding, BrandingServcice } from '@services/branding.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading = false;
  private originalTitle = document.title;
  private isUnloading = false;

  private brandingService = inject(BrandingServcice);
  private idAgency: string | undefined = undefined;

  constructor(
    private router: Router,
    private loadingService: LoadingService
  ) {
    this.router.events.subscribe(event => this.navigationInterceptor(event));
    this.loadingService.loading$.subscribe(loading => {
      this.isLoading = loading;
    });
  }

  ngOnInit(): void {
    this.loadAgencyId();
    this.loadBranding();

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  private loadAgencyId(): void {
    const urlId = this.getAgencyId();
    const storedId = sessionStorage.getItem('id');

    if (storedId) {
      const parsedId = JSON.parse(storedId);

      if (urlId && urlId !== parsedId) {
        sessionStorage.setItem('id', JSON.stringify(urlId));
        sessionStorage.removeItem('branding');
        this.idAgency = urlId;
      } else {
        this.idAgency = parsedId;
      }
    } else {
      this.idAgency = urlId;
      if (urlId) {
        sessionStorage.setItem('id', JSON.stringify(urlId));
      }
    }
  }

  private loadBranding(): void {
    if (!this.idAgency) {
      setBrandingColor('secondary-600', undefined, '#7E57C2');
      document.title = 'Tourbook';
      return;
    }

    const brandingRaw = sessionStorage.getItem('branding');
    const branding: Branding | null = brandingRaw
      ? JSON.parse(brandingRaw)
      : null;

    if (branding) {
      setBrandingColor('secondary-600', branding.secondary, '#7E57C2');
      this.setTitle(branding.name);
      if (branding?.favicon) this.setFavicon(branding.favicon);
      return;
    } else {
      this.brandingService.getBrandingAgency(this.idAgency).subscribe(res => {
        sessionStorage.setItem('branding', JSON.stringify(res));
        setBrandingColor('secondary-600', res.secondary, '#7E57C2');
        this.setTitle(res.name);
        if (res?.favicon) this.setFavicon(res.favicon);
      });
    }
  }

  private setFavicon(url: string): void {
    const link: HTMLLinkElement =
      document.querySelector("link[rel*='icon']") ||
      document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = url;
    document.head.appendChild(link);
  }

  private setTitle(name?: string): void {
    const newTitle = name?.trim() ? name : 'Tourbook';
    document.title = newTitle;
    this.originalTitle = newTitle;
  }

  getAgencyId(): string | undefined {
    const url = window.location.href;
    const query = url.split('?')[1];
    if (!query) return undefined;
    const decoded = decodeURIComponent(query);
    const parts = decoded.split('/');
    return parts[7]?.replace('=', '') || undefined;
  }

  private handleBeforeUnload = () => {
    this.isUnloading = true;
  };

  private handleVisibility = () => {
    if (this.isUnloading) return;

    const brandingRaw = sessionStorage.getItem('branding');
    const branding: Branding | null = brandingRaw
      ? JSON.parse(brandingRaw)
      : null;

    const baseTitle = branding?.name?.trim() || 'Tourbook';

    document.title = document.hidden
      ? `${baseTitle} | 👋¡Vuelve!`
      : this.originalTitle;
  };

  private navigationInterceptor(event: Event): void {
    if (event instanceof NavigationStart) this.isLoading = true;
    if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    )
      this.isLoading = false;
  }
}
